import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Project, TeamSettings } from '@claude-code-api/shared';
import { Bot } from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '@/components/ui';

interface ProjectTeamControlsCardProps {
  project: Project;
  onProjectUpdated?: () => void;
}

type WorkspaceTeamSettingsDraft = {
  executionMode: NonNullable<TeamSettings['executionMode']>;
  autoAssignIssues: boolean;
  autoStartQueuedTasks: boolean;
  notifyOnTaskComplete: boolean;
  notifyOnTaskFail: boolean;
  requireApprovalFor: NonNullable<TeamSettings['requireApprovalFor']>;
};

function normalizeTeamSettings(project: Project): WorkspaceTeamSettingsDraft {
  return {
    executionMode:
      project.teamSettings?.executionMode === 'auto' ? 'auto' : 'manual',
    autoAssignIssues: Boolean(project.teamSettings?.autoAssignIssues),
    autoStartQueuedTasks:
      project.teamSettings?.executionMode === 'auto' ||
      Boolean(project.teamSettings?.autoStartQueuedTasks),
    notifyOnTaskComplete: Boolean(project.teamSettings?.notifyOnTaskComplete),
    notifyOnTaskFail: Boolean(project.teamSettings?.notifyOnTaskFail),
    requireApprovalFor: project.teamSettings?.requireApprovalFor || 'none',
  };
}

export function ProjectTeamControlsCard({
  project,
  onProjectUpdated,
}: ProjectTeamControlsCardProps) {
  const { t } = useTranslation(['projects']);
  const [teamSettings, setTeamSettings] = useState<WorkspaceTeamSettingsDraft>(
    normalizeTeamSettings(project),
  );
  const [savingTeamSettings, setSavingTeamSettings] = useState(false);

  useEffect(() => {
    setTeamSettings(normalizeTeamSettings(project));
  }, [project]);

  const saveTeamSettings = async (nextSettings: WorkspaceTeamSettingsDraft) => {
    setSavingTeamSettings(true);
    try {
      const response = await fetch(`/api/projects/${project.id}/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ teamSettings: nextSettings }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        toast.error(
          data.error || t('projects:workflow.teamSettings.errors.save'),
        );
        return;
      }

      onProjectUpdated?.();
    } catch (error) {
      console.error('Failed to save team settings:', error);
      toast.error(t('projects:workflow.teamSettings.errors.save'));
    } finally {
      setSavingTeamSettings(false);
    }
  };

  const teamSettingsDirty =
    JSON.stringify(teamSettings) !==
    JSON.stringify(normalizeTeamSettings(project));

  useEffect(() => {
    if (!teamSettingsDirty) return undefined;

    const timeout = window.setTimeout(() => {
      void saveTeamSettings(teamSettings);
    }, 400);

    return () => window.clearTimeout(timeout);
  }, [teamSettings, teamSettingsDirty]);

  if (!project.isTeamMode) return null;

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold font-heading">
            {t('projects:workflow.teamSettings.title')}
          </h3>
        </div>
        {savingTeamSettings ? (
          <div className="text-xs text-muted-foreground">
            {t('projects:workflow.teamSettings.saving')}
          </div>
        ) : null}
      </div>

      <div className="divide-y divide-border/60 rounded-lg border border-border/60">
        <div className="flex flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <Label htmlFor="executionMode">
              {t('projects:workflow.teamSettings.executionMode.label')}
            </Label>
            <p className="text-sm text-muted-foreground">
              {t('projects:workflow.teamSettings.executionMode.description')}
            </p>
          </div>
          <div className="w-full md:w-48">
            <select
              id="executionMode"
              value={teamSettings.executionMode}
              onChange={(e) =>
                setTeamSettings((current) => ({
                  ...current,
                  executionMode: e.target.value === 'auto' ? 'auto' : 'manual',
                  autoStartQueuedTasks: e.target.value === 'auto',
                }))
              }
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="manual">
                {t('projects:workflow.teamSettings.executionMode.manual')}
              </option>
              <option value="auto">
                {t('projects:workflow.teamSettings.executionMode.auto')}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
